import { useNavigate, useLocation } from "react-router-dom"
import {
  Home,
  Search,
  History,
  CheckCircle,
  Clock,
  BarChart3,
  TrendingUp,
  Target,
  Activity,
  Settings,
  Shield,
  Users,
} from "lucide-react"

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
} from "@/components/ui/sidebar"
import { useAuth, PERMISSIONS } from "@/contexts/AuthContext"

const menuItems = [
  {
    title: "Overview",
    items: [
      {
        title: "Home",
        icon: Home,
        path: "/",
        permission: PERMISSIONS.VIEW,
      },
    ],
  },
  {
    title: "Scanning",
    items: [
      {
        title: "New Scan",
        icon: Search,
        path: "/new-scan",
        permission: PERMISSIONS.SCAN,
      },
      {
        title: "Scan History",
        icon: History,
        path: "/history",
        permission: PERMISSIONS.VIEW,
      },
      {
        title: "Review Queue",
        icon: CheckCircle,
        path: "/review-queue",
        permission: PERMISSIONS.VIEW,
      },
    ],
  },
  {
    title: "Automation",
    items: [
      {
        title: "Monitoring",
        icon: Activity,
        path: "/monitoring",
        permission: PERMISSIONS.AUTOMATION,
      },
      {
        title: "Scheduled Scans",
        icon: Clock,
        path: "/scheduled-scans",
        permission: PERMISSIONS.AUTOMATION,
      },
    ],
  },
  {
    title: "Analytics",
    items: [
      {
        title: "Search Visibility",
        icon: TrendingUp,
        path: "/visibility-trends",
        permission: PERMISSIONS.ANALYTICS,
      },
      {
        title: "Repeat Offenders",
        icon: Target,
        path: "/domain-analytics",
        permission: PERMISSIONS.ANALYTICS,
      },
    ],
  },
  {
    title: "System",
    items: [
      {
        title: "API Monitor",
        icon: BarChart3,
        path: "/api-monitor",
        permission: PERMISSIONS.ADMIN,
      },
      {
        title: "Settings",
        icon: Settings,
        path: "/settings",
        permission: PERMISSIONS.ADMIN,
      },
      {
        title: "User Management",
        icon: Users,
        path: "/user-management",
        permission: PERMISSIONS.ADMIN,
      },
    ],
  },
]

export function AppSidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { hasPermission } = useAuth()

  return (
    // Use offcanvas so collapse fully hides the menu on desktop
    <Sidebar collapsible="offcanvas">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-4 py-2">
          <Shield className="h-6 w-6" />
          <div className="flex flex-col">
            <span className="text-lg font-semibold">OSN+</span>
            <span className="text-xs text-muted-foreground">Piracy Detection</span>
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {menuItems.map((section) => {
          // Filter items based on permissions
          const visibleItems = section.items.filter(item => hasPermission(item.permission))

          // Only show section if it has visible items
          if (visibleItems.length === 0) return null

          return (
            <SidebarGroup key={section.title}>
              <SidebarGroupLabel>{section.title}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleItems.map((item) => {
                    const isActive = location.pathname === item.path
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          onClick={() => navigate(item.path)}
                          isActive={isActive}
                        >
                          <item.icon />
                          <span>{item.title}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )
        })}
      </SidebarContent>
    </Sidebar>
  )
}
